import copy from 'copy-to-clipboard';
import { ExternalLink, Link2, Monitor, RotateCw, Smartphone } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@dashboard/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { toast } from '@dashboard/context/ToastContext';
import { cn } from '@dashboard/lib/utils';

import useEmailTemplatesUrl, { type PreviewWidth } from './useEmailTemplatesUrl';

type StageTab = { value: string; label: string };

type PreviewStageProps = {
  title: string;
  header: React.ReactNode;
  tabs?: StageTab[];
  tabsLabel?: string;
  activeTab?: string;
  onTabChange?: (value: string) => void;
  html?: string;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  onReload: () => void;
  placeholder?: React.ReactNode;
};

const FRAME_WIDTHS: Record<PreviewWidth, string> = {
  desktop: 'max-w-[960px]',
  mobile: 'max-w-[375px]',
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// A blob URL runs with the dashboard's origin, so the email goes in a sandboxed
// frame where any script in it cannot reach the admin session.
function openInNewTab(html: string, title: string) {
  const page = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>html,body{margin:0;height:100%}iframe{display:block;width:100%;height:100%;border:0}</style></head><body><iframe sandbox="allow-popups allow-popups-to-escape-sandbox" srcdoc="${escapeHtml(html)}"></iframe></body></html>`;
  const url = URL.createObjectURL(new Blob([page], { type: 'text/html' }));
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function errorDetails(error: unknown) {
  const body = (error ?? {}) as { message?: unknown; stack?: unknown };
  const message =
    typeof body.message === 'string' && body.message ? body.message : 'Something went wrong.';
  // A thrown Error carries the dashboard's own stack, which says nothing about the template.
  const stack = !(error instanceof Error) && typeof body.stack === 'string' ? body.stack : null;
  return { message, stack };
}

// A scrollbar in the still-short frame narrows the email and makes it taller, so hide it first.
// Not scrollHeight: it rounds down and never drops below the frame's current height.
const emailHeight = (frame: HTMLIFrameElement) => {
  const root = frame.contentDocument?.documentElement;
  if (!root) return undefined;
  root.style.overflow = 'hidden';
  return Math.ceil(root.getBoundingClientRect().height);
};

function EmailFrame({ html, title }: { html: string; title: string }) {
  const [height, setHeight] = useState<number>();

  return (
    <div className="overflow-hidden rounded-md border border-border bg-white">
      <iframe
        title={title}
        srcDoc={html}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        onLoad={event => setHeight(emailHeight(event.currentTarget))}
        style={height ? { height } : undefined}
        className="block min-h-[480px] w-full border-0"
      />
    </div>
  );
}

function RenderError({ error }: { error: unknown }) {
  const { message, stack } = errorDetails(error);

  return (
    <div
      role="alert"
      className="w-full max-w-[960px] rounded-md border border-destructive/30 bg-background p-4 text-sm"
    >
      <p className="font-medium text-destructive">{message}</p>
      {stack && (
        <pre className="mt-3 max-h-[480px] overflow-auto text-xs whitespace-pre-wrap break-words text-muted-foreground">
          {stack}
        </pre>
      )}
    </div>
  );
}

export default function PreviewStage({
  title,
  header,
  tabs = [],
  tabsLabel = 'Variants',
  activeTab,
  onTabChange,
  html,
  isLoading,
  isFetching,
  error,
  onReload,
  placeholder,
}: PreviewStageProps) {
  const { width, update } = useEmailTemplatesUrl();

  const copyLink = () => {
    copy(window.location.href);
    toast({ title: 'Link copied' });
  };

  const body = (() => {
    if (placeholder) return placeholder;
    if (error) return <RenderError error={error} />;
    if (isLoading || html == null) {
      return <Skeleton className={cn('h-[480px] w-full', FRAME_WIDTHS[width])} />;
    }
    return (
      <div
        className={cn('w-full transition-opacity', FRAME_WIDTHS[width], isFetching && 'opacity-50')}
      >
        {/* Remounting on a width change measures the email's height again at the new width. */}
        <EmailFrame key={width} html={html} title={title} />
      </div>
    );
  })();

  return (
    <>
      <header className="space-y-3 border-b border-border bg-background p-4">
        {header}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {tabs.length > 1 ? (
            <Select value={activeTab} onValueChange={onTabChange}>
              <SelectTrigger
                aria-label={tabsLabel}
                size="sm"
                className="max-w-full min-w-0 sm:max-w-md"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tabs.map(tab => (
                  <SelectItem key={tab.value} value={tab.value}>
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              aria-label="Preview width"
              value={width}
              onValueChange={value => value && update({ width: value })}
            >
              <ToggleGroupItem value="desktop">
                <Monitor />
                Desktop
              </ToggleGroupItem>
              <ToggleGroupItem value="mobile">
                <Smartphone />
                Mobile
              </ToggleGroupItem>
            </ToggleGroup>
            <Button variant="outline" size="sm" onClick={onReload} disabled={Boolean(placeholder)}>
              <RotateCw className={cn(isFetching && 'animate-spin')} />
              Reload
            </Button>
            <Button variant="outline" size="sm" onClick={copyLink}>
              <Link2 />
              Copy link
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => html && openInNewTab(html, title)}
              disabled={!html || Boolean(placeholder)}
            >
              <ExternalLink />
              Open in new tab
            </Button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 items-start justify-center overflow-auto p-4">{body}</div>
    </>
  );
}
